import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import SiteHeader from "../components/SiteHeader";
import Footer from "../components/Footer";

const WebPortal = () => {
  const location = useLocation();

  // ระบบดักจับการเปลี่ยนหน้าและทำการเลื่อนหน้าจออัตโนมัติ
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      const element = document.getElementById(id);
      
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 300);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  // โครงสร้างข้อมูลแหล่งข้อมูลภายนอก (ภาพแบนเนอร์ 6 - 11)
  const externalDatabases = [
    {
      id: "ext-sac",
      title: "ฐานข้อมูลเอกสารตัวเขียนในประเทศไทย ศูนย์มานุษยวิทยาสิรินธร (องค์การมหาชน) (ศมส) ",
      category: "ในประเทศ",
      url: "https://manuscripts.sac.or.th/",
      image: "/icons/6.png",
      fallbackColor: "bg-[#7d6b5d]"
    },
    {
      id: "ext-cmru",
      title: "คลังข้อมูลดิจิทัล (Digital Repository) สำนักศิลปะและวัฒนธรรม มหาวิทยาลัยราชภัฏเชียงใหม่ เพื่อการอนุรักษ์และเผยแพร่ภาพถ่ายคัมภีร์ใบลาน พับสา เอกสาร อักษรตระกูลไท และสื่อดิจิทัลอื่น ๆ จากพื้นที่ลุ่มน้ำโขงและสาละวิน ครอบคลุมภาคเหนือของไทย เมียนมา จีน และลาว",
      category: "ในประเทศ",
      url: "https://culture.cmru.ac.th/digitalrepository/",
      image: "/icons/7.png",
      fallbackColor: "bg-[#6c5a4d]"
    },
    {
      id: "ext-efeo",
      title: "EFEO Lanna Manuscripts โครงการนี้เป็นความร่วมมือระหว่างสำนักฝรั่งเศสแห่งปลายบุรพทิศ (EFEO) และศูนย์มานุษยวิทยาสิรินธร (SAC)",
      category: "ในประเทศ",
      url: "https://lanna-manuscripts.efeo.fr/manuscripts",
      image: "/icons/8.png",
      fallbackColor: "bg-[#5b4a3e]"
    },
    {
      id: "ext-bdrc",
      title: "Internet Archive แหล่งสืบค้นทรัพยากรดิจิทัลแบบเปิด ให้บริการคลังข้อมูลหลากหลายประเภท ทั้งเอกสารข้อความ หนังสือ สื่อภาพและเสียง รวมถึงประวัติศาสตร์หน้าเว็บไซต์ เพื่อสนับสนุนการศึกษาและการค้นคว้า",
      category: "ต่างประเทศ",
      url: "https://archive.org/details/bdrc-fplmanuscripts",
      image: "/icons/9.png",
      fallbackColor: "bg-[#d8653b]"
    },
    {
      id: "ext-seadl",
      title: "SEADL: Southeast Asia Digital Library แหล่งสืบค้นทรัพยากรดิจิทัลแบบเปิดที่รวบรวมข้อมูลเกี่ยวกับภูมิภาคเอเชียตะวันออกเฉียงใต้",
      category: "ต่างประเทศ",
      url: "https://sea.lib.niu.edu/islandora/object/SEAImages:KKManuscripts",
      image: "/icons/10.png",
      fallbackColor: "bg-[#c45a33]"
    },
    {
      id: "ext-mmdl",
      title: "MMDL (University of Toronto) คลังข้อมูลภาพถ่ายดิจิทัลคัมภีร์ใบลานกว่า 700 รายการจากหอสมุด U Pho Thi ประเทศเมียนมา",
      category: "ต่างประเทศ",
      url: "https://mmdl.utoronto.ca/databases/",
      image: "/icons/11.png",
      fallbackColor: "bg-[#a64a28]"
    }
  ];

  return (
    <div className="min-h-screen bg-[#fcfaf8] flex flex-col font-sans">
      <SiteHeader />

      <main className="flex-grow py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          
          {/* ส่วนหัวของหน้า */}
          <div className="text-center mb-14">
            <h1 className="text-3xl md:text-4xl font-bold text-[#5b4a3e] inline-block relative pb-4">
              ฐานข้อมูลเอกสารโบราณและหนังสือหายาก
              <span className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-20 h-1 bg-[#d8653b] rounded-full"></span>
            </h1>
            
          </div>

          {/* นำเสนอข้อมูลแบบ Grid 2 คอลัมน์ (แบนเนอร์ 16:9) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {externalDatabases.map((db, index) => (
              <a 
                key={index}
                id={db.id}
                href={db.url} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="block bg-white rounded-2xl shadow-sm border border-[#e5d7ca] hover:shadow-lg hover:border-[#d8653b]/60 transition-all duration-300 overflow-hidden group transform hover:-translate-y-1"
              >
                {/* 
                  พื้นที่รูปภาพ Banner สัดส่วน 16:9 (aspect-video)
                  ใช้ object-left-top เพื่อเน้นชิดมุมซ้ายบน และปล่อยให้มุมขวาล่างครอปหายไป
                */}
                <div className={`w-full aspect-video ${db.fallbackColor} relative overflow-hidden flex items-center justify-center`}>
                  <img 
                    src={db.image} 
                    alt={db.title} 
                    className="absolute inset-0 w-full h-full object-cover object-left-top group-hover:scale-105 transition-transform duration-700 z-10"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  {/* Fallback View กรณีรูปภาพยังไม่พร้อม */}
                  <div className="relative z-0 text-white text-center p-6 w-full">
                    <h3 className="text-xl font-bold mb-2 shadow-sm">{db.title}</h3>
                    <p className="opacity-80 text-sm">(รอภาพแบนเนอร์)</p>
                  </div>
                  {/* Overlay สำหรับเอฟเฟกต์ตอน Hover */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300 z-20"></div>
                </div>

                {/* พื้นที่ข้อความและรายละเอียดด้านล่างภาพ */}
                <div className="p-5 md:p-6 bg-white relative z-30 flex flex-col h-full">
                  <div className="mb-3">
                    <span className="text-xs font-semibold px-2.5 py-1 bg-[#f1e6da] text-[#d8653b] rounded-md">
                      {db.category}
                    </span>
                  </div>
                  <h3 className="text-[#5b4a3e] font-medium text-lg md:text-xl group-hover:text-[#d8653b] transition-colors flex items-start justify-between gap-3 leading-tight">
                    <span className="line-clamp-2">{db.title}</span>
                    <ExternalLink size={20} className="flex-shrink-0 opacity-50 group-hover:opacity-100 transition-opacity mt-0.5" />
                  </h3>
                </div>
              </a>
            ))}
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
};

export default WebPortal;